import { getDecimalSeparator } from '@msc-b2c/shared-utils';

// eslint-disable-next-line @typescript-eslint/ban-types
export const waitForSeparator = async (getter: Function): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const interval = setInterval(() => {
      const separator = getter();
      if (separator) {
        clearInterval(interval);
        resolve(separator);
      }
    }, 200);

    setTimeout(() => {
      clearInterval(interval);
      reject(new Error('Timeout: Separator not found.'));
    }, 20000); // Timeout after 20 seconds
  });
};

export const sectionNumbersLogic = async (element: HTMLElement) => {
  // Disable animation if -no-animation class is present on element or parent containers
  const hasNoAnimationClass = element.classList.contains('-no-animation');
  const parentHasNoAnimation = element.closest('.container.-no-animation') !== null;

  if (hasNoAnimationClass || parentHasNoAnimation) {
    displayNumbersInstantly(element);
    return;
  }

  try {
    const decimalSeparator = await waitForSeparator(getDecimalSeparator);

    const callback: IntersectionObserverCallback = (
      entries: IntersectionObserverEntry[],
      observer: IntersectionObserver
    ) => {
      entries.forEach((entry: IntersectionObserverEntry) => {
        if (entry.isIntersecting) {
          animatedNumbersLogic(entry.target as HTMLElement, decimalSeparator);
          observer.unobserve(entry.target);
        }
      });
    };

    const observer: IntersectionObserver = new IntersectionObserver(callback, {
      root: null, // Use the viewport as the root
      rootMargin: '0px',
      threshold: 0.1, // Trigger the callback when 10% of the element is in view
    });

    observer.observe(element);
  } catch (error) {
    console.error(error);
  }
};

function displayNumbersInstantly(container: HTMLElement) {
  const textParagraph = container.querySelector('.cmp-text');
  const paragraphElements = textParagraph ? Array.from(textParagraph.querySelectorAll('p')) : null;
  if (!paragraphElements) return;

  paragraphElements.forEach((targetElement) => {
    if (!targetElement?.textContent) return;
    targetElement.style.opacity = '1';
  });
}

function animatedNumbersLogic(container: HTMLElement, localeDecimalSeparator: string) {
  const textParagraph = container.querySelector('.cmp-text');
  const paragraphElements = textParagraph ? Array.from(textParagraph.querySelectorAll('p')) : null;
  if (!paragraphElements) return;

  paragraphElements.forEach((targetElement) => {
    if (!targetElement?.textContent) return;

    const { numbers, text, decimalIndex, decimalSeparator } = processInput(
      targetElement.textContent.trim()
    );

    if (!numbers) return;

    const usedDecimalSeparator = decimalSeparator ?? localeDecimalSeparator;
    const usedThousandSeparator = usedDecimalSeparator === ',' ? '.' : ',';

    const end = Number(numbers);
    const currentNumber = 0;
    targetElement.textContent = `${currentNumber}`;
    animateNumber(
      currentNumber,
      end,
      2000,
      currentNumber,
      targetElement,
      decimalIndex,
      text,
      usedDecimalSeparator,
      usedThousandSeparator
    );
  });
}

function animateNumber(
  start: number,
  end: number,
  duration: number,
  currentNumber: number,
  target: HTMLElement,
  decimalIndex: number | null,
  text: string | null,
  decimalSeparator: string,
  thousandSeparator: string
) {
  const startTime = performance.now();

  function updateNumber(currentTime: DOMHighResTimeStamp) {
    const elapsedTime = currentTime - startTime;
    const progress = Math.min(elapsedTime / duration, 1);
    currentNumber = Math.floor(progress * (end - start) + start);

    target.textContent =
      currentNumber < 0
        ? '0'
        : `${addDots(currentNumber, decimalIndex, end, decimalSeparator, thousandSeparator)} ${
            text ?? ''
          }`;

    if (progress < 1) requestAnimationFrame(updateNumber);
  }

  requestAnimationFrame(updateNumber);
}

function addDots(
  number: number,
  decimalIndex: number | null,
  end: number,
  decimalSeparator: string,
  thousandSeparator: string
): string {
  const numString = number.toString();
  const endString = end.toString();

  if (decimalIndex !== null) {
    const decimalStartIndex = endString.length - decimalIndex;
    const decimalSpots = numString.length - decimalStartIndex;
    if (decimalSpots <= 0) {
      return '0' + decimalSeparator + number;
    }
    const integerPart = numString.slice(0, -decimalStartIndex);
    const decimalPart = numString.slice(-decimalStartIndex);
    const integerWithSep = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, `${thousandSeparator}`);
    return integerWithSep + decimalSeparator + decimalPart;
  }
  // Use a regular expression to add dots every 3 digits (starting from the right)
  return numString.replace(/\B(?=(\d{3})+(?!\d))/g, `${thousandSeparator}`);
}

function processInput(input: string): {
  numbers: string;
  text: string | null;
  decimalIndex: number | null;
  decimalSeparator: string | null;
} {
  const numberMatch = RegExp(/[\d.,]+/).exec(input)?.[0] ?? '';
  const lastDot = input.lastIndexOf('.');
  const lastComma = input.lastIndexOf(',');

  let decimalSeparator: string | null = null;
  let decimalIndex: number | null = null;

  if (lastComma > lastDot) {
    decimalSeparator = ',';
    decimalIndex = input.indexOf(',');
  } else if (lastDot > lastComma) {
    decimalSeparator = '.';
    decimalIndex = input.indexOf('.');
  }

  const firstLetter = findFirstLetterIndex(input);
  const text = firstLetter !== null ? input.slice(firstLetter) : null;

  return {
    numbers: numberMatch.replace(/[.,]/g, ''),
    text,
    decimalIndex,
    decimalSeparator,
  };
}

function findFirstLetterIndex(input: string): number | null {
  const idx = input.search(/[a-zA-Z/]|%/);
  return idx !== -1 ? idx : null;
}
